import { useToast } from '@lifebit-ai/lds'
import { UseQueryOptions, useQuery } from '@tanstack/react-query'
import { HttpStatusCodes } from 'enums'

import { useOnQueryError } from 'hooks/useOnQueryError'
import { getImportRequestVerification } from 'services/import-requests'
import { ImportRequestQueryKeys } from 'services/import-requests/queryKeys'
import type { ImportRequestVerificationDetail } from 'services/import-requests/types'
import { ApiErrorResponse } from 'services/types'

type ImportRequestVerificationQueryOptions = Omit<
  UseQueryOptions<ImportRequestVerificationDetail, ApiErrorResponse>,
  'queryKey'
> & {
  onError?: (error: ApiErrorResponse) => void
}

interface UseGetImportRequestVerificationParams {
  requestId: string
  options?: ImportRequestVerificationQueryOptions
}

export const useGetImportRequestVerification = ({ requestId, options }: UseGetImportRequestVerificationParams) => {
  const toast = useToast()
  const { onError, ...queryOptions } = options ?? {}

  const query = useQuery<ImportRequestVerificationDetail, ApiErrorResponse>({
    ...queryOptions,
    queryKey: ImportRequestQueryKeys.Verification(requestId),
    queryFn: () => getImportRequestVerification(requestId),
    enabled: queryOptions.enabled ?? !!requestId
  })

  useOnQueryError(query, error => {
    if (onError) {
      onError(error)
      return
    }

    if (error.response?.status === HttpStatusCodes.NotFound) return

    toast({ message: 'Failed to get import request details', type: 'error' })
  })

  return query
}
